import React from "react";
import styled, { css } from "styled-components";
import _ from "lodash";
import {
  useHistory,
  useRouteMatch
} from "react-router-dom";
import { useMappedState } from "redux-react-hook";

import {
  OrderPaymentStatus,
  IOrder
} from "src/core/orders/api/orders.api";
import { Text } from "src/library/components/Text";
import { Datetime } from "src/library/components/Datetime";
import { ITerminology } from "src/library/utils";
import { formatPrice } from "src/core/prices/utils";
import { Button } from "src/library/components/Button";
import {
  calculateCartTotalQuantity,
  calculatePriceTotals
} from "src/core/orders/utils/orders";
import { useOrderCutoffTime } from "src/core/orders/hooks/validation";
import {
  usePaymentApi,
  IPaymentCard
} from "src/modules/payment/api/payment.api";
import { ErrorNotice } from "src/core/shared/components/ErrorNotice";
import { PaymentTransaction } from "src/modules/payment/api/transactions.api";
import { ThreeDSecureModal } from "src/modules/payment/components/ThreeDSecureModal";
import { useOrder } from "src/core/orders/hooks";
import { InfoBar } from "src/library/components/InfoBar";
import { useDrop } from "src/core/drops/hooks";
import { IStore } from "src/core/shared/store";

import PayOrderLaterWarningModal from "./PayOrderLaterWarningModal";

export interface PayOrderSummeryProps {
  terminology: ITerminology;
  order?: IOrder;
  selectedPaymentCard: IPaymentCard | null;
}

// Displays a summary of an order with options to pay now or later
const PayOrderSummery: React.FunctionComponent<PayOrderSummeryProps> = ({
  terminology, order, selectedPaymentCard
}) => {
  const history = useHistory();
  const [ showPayLaterWarningModal, setShowPayLaterWarningModal ] = React.useState(false);
  const orderCutoffTime = useOrderCutoffTime();
  const match = useRouteMatch<{ orderId?: string }>();
  const [ { drop } ] = useDrop();
  const { orderStatuses } = useMappedState((store: IStore) => ({ orderStatuses: store.global.settings ? store.global.settings.orderStatuses : [] }));

  const orderId = React.useMemo(() => {
    return match.params.orderId ? parseInt(match.params.orderId as string, 0) : null;
  }, [ match.params.orderId ]);

  const [ { currentOrder }, { refreshOrder } ] = useOrder(
    orderId,
    order,
    false
  );

  const price = React.useMemo(() => (
    calculatePriceTotals(currentOrder?.data?.orderLines || [], drop)
  ), [ currentOrder?.data?.orderLines, drop ]);

  // On mount, refresh the order to ensure we've got the most up-to-date copy
  React.useEffect(() => {
    refreshOrder();
  // order api legacy - causes render loop so left out of deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ ]);

  const [ currentTransaction, setCurrentTransaction ] = React.useState<PaymentTransaction | null>(null);
  const [ payForOrderRes, payForOrderReq ] = usePaymentApi("CREATE_ORDER_TRANSACTION");

  const paymentRequiredBy = React.useMemo(() => {
    if (currentOrder?.data) {
      return orderCutoffTime(currentOrder.data);
    }

    return null;
  }, [ currentOrder?.data, orderCutoffTime ]);

  const summaryInfobarItems = React.useMemo(() => {
    const sidebarItems = _.compact([
      {
        label: `${_.startCase(terminology.order.singular.text)} Delivery Date:`,
        value: currentOrder.data?.orderDeliveryDate ? (
          <Datetime
            ignoreLocal
            template="long_date"
            date={currentOrder.data.orderDeliveryDate || null}
          />
        ) : "-"
      },
      currentOrder?.data ? {
        label: "PO Number:",
        value: currentOrder?.data?.customerReference || "-"
      } : null,
      {
        label: `Subtotal (${calculateCartTotalQuantity(currentOrder?.data?.orderLines || [])} products):`,
        value: (
          <Text weight="bold">
            {formatPrice(price.net, 2, drop?.priceCode?.currency)}
          </Text>
        )
      },
      {
        label: "Vat:",
        value: (
          <Text>
            {formatPrice(price.vat, 2, drop?.priceCode?.currency)}
          </Text>
        )
      }
    ]);

    return sidebarItems;
  }, [
    terminology.order.singular.text,
    currentOrder.data,
    price
  ]);

  React.useEffect(() => {
    if (payForOrderRes.data) {
      setCurrentTransaction(payForOrderRes.data);
    } else {
      setCurrentTransaction(currentOrder?.data?.transactions ? currentOrder?.data?.transactions[ 0 ] : null);
    }
  }, [ currentOrder?.data?.transactions, payForOrderRes.data ]);

  const canPayForOrderMessage = React.useMemo(() => {
    if (currentOrder?.data) {
      if (currentOrder.data.paymentStatus && [
        OrderPaymentStatus.CAPTURED,
        OrderPaymentStatus.PARTIAL_REFUNDED,
        OrderPaymentStatus.REFUNDED,
        OrderPaymentStatus.REFUND_FAILED
      ].includes(currentOrder.data.paymentStatus)) {
        return "You have already paid for this order";
      }
    }

    return null;
  }, [ currentOrder.data ]);

  const payForOrder = React.useCallback(() => {
    if (currentOrder?.data?.id && selectedPaymentCard?.id) {
      payForOrderReq({
        orderId: currentOrder.data.id,
        paymentCardId: selectedPaymentCard.id
      });
    }
  }, [
    currentOrder,
    payForOrderReq,
    selectedPaymentCard
  ]);

  React.useEffect(() => {
    if (payForOrderRes.error) {
      setCurrentTransaction(null);
    } else if (payForOrderRes.data) {
      if (!payForOrderRes.data.nextAction) {
        history.push(`/orders/${orderId}/order-placed`);
      }
    }
  }, [
    history,
    orderId,
    payForOrderRes.data,
    payForOrderRes.error
  ]);

  const isCancelledOrder = React.useMemo(() => {
    const currentOrderStatus = orderStatuses.find(status => status.id === currentOrder.data?.orderStatus?.id);

    if (currentOrderStatus?.settings?.group === "CANCELLED") {
      return "This order has been cancelled and can no longer be processed.";
    }

    return null;
  }, [ currentOrder.data?.orderStatus?.id, orderStatuses ]);

  const currentErrorMessage = React.useMemo(() => {
    if (payForOrderRes.error) {
      return payForOrderRes.error.message;
    }

    if (currentTransaction?.errorReason?.message) {
      return currentTransaction?.errorReason?.message;
    }

    if (isCancelledOrder) {
      return isCancelledOrder;
    }

    return null;
  }, [
    currentTransaction?.errorReason?.message,
    isCancelledOrder,
    payForOrderRes.error
  ]);

  return (
    <StyledWrapper>
      <InfoBar
        title="Summary"
        items={summaryInfobarItems}
        listBorderBottom
        disableExpand
      >
        <div className="order-total">
          <div className="item-text">
            <Text as="h3">
              {`${_.startCase(terminology.order.singular.text)} Total:`}
            </Text>
          </div>

          <div className="item-text">
            <Text as="h3">
              {formatPrice(price.total, 2, drop?.priceCode?.currency)}
            </Text>
          </div>
        </div>

        <div className="action-buttons">
          <Button
            onClick={() => payForOrder()}
            $buttonType="primary"
            loading={payForOrderRes.pending}
            disabled={!selectedPaymentCard?.id || !currentOrder?.data || !!canPayForOrderMessage || !!isCancelledOrder}
          >
            Confirm and pay
          </Button>

          {canPayForOrderMessage && (
            <ErrorNotice
              type="warn"
              message={canPayForOrderMessage}
            />
          )}

          {/* dont allow pay later on made to order orders */}
          {currentOrder?.data?.orderDeliveryDate && (
            <Button
              loading={payForOrderRes.pending}
              disabled={!!canPayForOrderMessage || !!isCancelledOrder}
              onClick={() => setShowPayLaterWarningModal(true)}
              $buttonType="secondary"
            >
              Pay later
            </Button>
          )}

          {currentErrorMessage && (
            <ErrorNotice
              type="error"
              message={currentErrorMessage}
            />
          )}

          <Button
            disabled={!currentOrder?.data}
            onClick={() => currentOrder.data && history.push(`/orders/${(currentOrder.data).id}`, { edit: true })}
            $buttonType="plain"
          >
            Back To Edit Order
          </Button>
        </div>
      </InfoBar>

      {currentOrder?.data && (
        <PayOrderLaterWarningModal
          open={showPayLaterWarningModal}
          onDismiss={() => setShowPayLaterWarningModal(false)}
          order={currentOrder.data}
          paymentRequiredBy={paymentRequiredBy}
        />
      )}

      {currentOrder?.data && (
        <ThreeDSecureModal
          handleError={(transaction, stripeError) => {
            if (transaction) {
              setCurrentTransaction(transaction);
            } else if (stripeError) {
              refreshOrder();
            }
          }}
          onSuccess={() => {
            history.push(`/orders/${orderId}/order-placed`);
          }}
          secureUrl={currentTransaction?.nextAction?.action.url || null}
        />
      )}
    </StyledWrapper>

  );
};

const StyledWrapper = styled.div`
  ${({ theme }) => css`
    .info-bar-content-wrapper {
      min-width: 380px;
    }

    .order-total {
      padding: ${theme.spacing.padding.sm} ${theme.spacing.padding.lg};
      display: flex;
      align-content: center;
      
      .item-text {
        display: flex;
        justify-content: flex-end;
        flex: 1;
      }

      h3 {
        margin: 0;
      }
    }

    .action-buttons {
      padding: ${theme.spacing.padding.md};

      button {
        margin: ${theme.spacing.margins.sm} 0;
        width: 100%;
      }
    }

    @media (min-width: 1000px) {
      .info-wrapper {
        min-height: inherit;
        height: inherit;
        padding-bottom: 0;
      }
    }
  `}
`;

export default PayOrderSummery;
